const STORAGE_KEY = 'customlooks:wishlist:v1';
const CHANGE_EVENT = 'customlooks:wishlist-changed';
const MAX_ITEMS = 100;

let memoryItems = [];
const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

/**
 * Keep only safe Shopify product handles, preserve order, and remove duplicates.
 *
 * @param {unknown} value
 * @returns {string[]}
 */
export function normalizeWishlist(value) {
  if (!Array.isArray(value)) return [];

  const items = [];
  const seen = new Set();

  for (const candidate of value) {
    if (typeof candidate !== 'string') continue;

    const handle = candidate.trim().toLowerCase();
    if (!/^[a-z0-9](?:[a-z0-9-]{0,253}[a-z0-9])?$/.test(handle) || seen.has(handle)) continue;

    seen.add(handle);
    items.push(handle);
    if (items.length === MAX_ITEMS) break;
  }

  return items;
}

/**
 * Toggle a product handle without mutating the supplied array.
 *
 * @param {string[]} currentItems
 * @param {string} candidate
 * @returns {{ items: string[], added: boolean }}
 */
export function toggleWishlistItem(currentItems, candidate) {
  const items = normalizeWishlist(currentItems);
  const normalizedCandidate = normalizeWishlist([candidate])[0];

  if (!normalizedCandidate) return { items, added: false };

  if (items.includes(normalizedCandidate)) {
    return { items: items.filter((handle) => handle !== normalizedCandidate), added: false };
  }

  return {
    items: [normalizedCandidate, ...items].slice(0, MAX_ITEMS),
    added: true,
  };
}

function readItems() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    memoryItems = normalizeWishlist(parsed);
  } catch {
    // Storage can be unavailable in strict privacy modes. Keep the feature
    // usable for the current page without leaking data elsewhere.
  }

  return [...memoryItems];
}

function writeItems(items) {
  memoryItems = normalizeWishlist(items);

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryItems));
  } catch {
    // The in-memory fallback is intentional when storage is unavailable.
  }

  document.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, {
      detail: { items: [...memoryItems] },
    })
  );

  return [...memoryItems];
}

class WishlistToggle extends HTMLElementBase {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = normalizeWishlist([this.dataset.productHandle])[0] || '';
    if (!this.button || !this.handle) return;

    this.handleClick = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const result = toggleWishlistItem(readItems(), this.handle);
      writeItems(result.items);
      this.render(result.items);
    };
    this.handleChange = (event) => this.render(event.detail?.items || readItems());

    this.button.addEventListener('click', this.handleClick);
    document.addEventListener(CHANGE_EVENT, this.handleChange);
    this.render(readItems());
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
    document.removeEventListener(CHANGE_EVENT, this.handleChange);
  }

  render(items) {
    if (!this.button) return;

    const active = items.includes(this.handle);
    const label = active ? this.dataset.activeLabel : this.dataset.inactiveLabel;
    const text = this.querySelector('[data-wishlist-label]');

    this.button.setAttribute('aria-pressed', String(active));
    this.button.setAttribute('aria-label', label || 'Favorite');
    this.button.title = label || 'Favorite';
    if (text) text.textContent = label || 'Favorite';
  }
}

class WishlistCount extends HTMLElementBase {
  connectedCallback() {
    this.handleChange = (event) => this.render(event.detail?.items || readItems());
    document.addEventListener(CHANGE_EVENT, this.handleChange);
    this.render(readItems());
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.handleChange);
  }

  render(items) {
    const count = items.length;
    const value = this.querySelector('[data-wishlist-count-value]');

    this.hidden = count === 0;
    this.setAttribute('aria-label', `${count} produse favorite`);
    if (value) value.textContent = count > 99 ? '99+' : String(count);
  }
}

class WishlistView extends HTMLElementBase {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.loading = this.querySelector('[data-wishlist-loading]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.error = this.querySelector('[data-wishlist-error]');
    this.currency = this.dataset.currency || 'RON';
    this.renderVersion = 0;

    this.handleChange = () => this.render();
    document.addEventListener(CHANGE_EVENT, this.handleChange);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.handleChange);
  }

  async render() {
    const version = ++this.renderVersion;
    const handles = readItems();

    this.grid?.replaceChildren();
    if (this.error) this.error.hidden = true;

    if (handles.length === 0) {
      if (this.loading) this.loading.hidden = true;
      if (this.empty) this.empty.hidden = false;
      return;
    }

    if (this.loading) this.loading.hidden = false;
    if (this.empty) this.empty.hidden = true;

    const products = await Promise.all(handles.map((handle) => this.fetchProduct(handle)));
    if (version !== this.renderVersion) return;

    if (this.loading) this.loading.hidden = true;

    let rendered = 0;
    products.forEach((product, index) => {
      if (!product || !this.grid) return;
      this.grid.append(this.buildCard(product, handles[index]));
      rendered += 1;
    });

    if (rendered === 0 && this.error) this.error.hidden = false;
  }

  async fetchProduct(handle) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  buildCard(product, handle) {
    const article = document.createElement('article');
    article.className = 'wishlist-card';

    const link = document.createElement('a');
    link.className = 'wishlist-card__media';
    link.href = product.url || `/products/${handle}`;
    link.setAttribute('aria-label', product.title || 'Vezi produsul');

    if (product.featured_image) {
      const image = document.createElement('img');
      image.src = this.resizeImage(product.featured_image, 600);
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.width = 600;
      image.height = 600;
      link.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'wishlist-card__placeholder';
      placeholder.textContent = 'Fără imagine';
      link.append(placeholder);
    }

    const content = document.createElement('div');
    content.className = 'wishlist-card__content';

    const title = document.createElement('a');
    title.className = 'wishlist-card__title';
    title.href = link.href;
    title.textContent = product.title || handle;

    const price = document.createElement('p');
    price.className = 'wishlist-card__price';
    price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'ro-RO', {
      style: 'currency',
      currency: this.currency,
    }).format(Number(product.price || 0) / 100);

    const actions = document.createElement('div');
    actions.className = 'wishlist-card__actions';

    const view = document.createElement('a');
    view.className = 'button button-secondary wishlist-card__view';
    view.href = link.href;
    view.textContent = 'Vezi produsul';

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'button-unstyled wishlist-card__remove';
    remove.textContent = 'Elimină';
    remove.setAttribute('aria-label', `Elimină ${product.title || handle} din favorite`);
    remove.addEventListener('click', () => {
      writeItems(readItems().filter((item) => item !== handle));
    });

    actions.append(view, remove);
    content.append(title, price, actions);
    article.append(link, content);
    return article;
  }

  resizeImage(url, width) {
    try {
      const imageUrl = new URL(url, window.location.origin);
      imageUrl.searchParams.set('width', String(width));
      return imageUrl.toString();
    } catch {
      return url;
    }
  }
}

if (typeof window !== 'undefined' && 'customElements' in window) {
  if (!customElements.get('wishlist-toggle')) customElements.define('wishlist-toggle', WishlistToggle);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
  if (!customElements.get('wishlist-view')) customElements.define('wishlist-view', WishlistView);

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    try {
      memoryItems = normalizeWishlist(JSON.parse(event.newValue || '[]'));
    } catch {
      memoryItems = [];
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: [...memoryItems] } }));
  });
}
